// trackChanges.js
// WORD-GRADE TRACKED CHANGES.
//
// Every edit, while track-changes is ON, is captured as a revision carrying:
//   author (username), authorId, timestamp (exact ISO time), type (ins|del),
//   and an optional reason. These map to native Word <w:ins>/<w:del> on export
//   and feed the audit trail (who / when / what / why).
//
// Implementation: insertion/deletion are Tiptap marks. A ProseMirror plugin
// (appendTransaction) auto-applies the insertion mark to ANY typed/pasted text
// and converts deletions into "marked deleted" (struck through, not removed)
// so a reviewer can accept/reject — exactly like Word.
 
import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
 
const authorAttrs = {
  author:   { default: 'Unknown' },
  authorId: { default: 'unknown' },
  date:     { default: () => new Date().toISOString() },
  reason:   { default: '' },
};
 
const renderTitle = (a, verb) => {
  const when = a.date ? new Date(a.date).toLocaleString() : '';
  const why = a.reason ? ` — reason: ${a.reason}` : '';
  return `${verb} by ${a.author} at ${when}${why}`;
};
 
export const Insertion = Mark.create({
  name: 'insertion',
  inclusive: true,
  addAttributes() { return authorAttrs; },
  parseHTML() { return [{ tag: 'ins' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['ins', mergeAttributes(HTMLAttributes, {
      class: 'tc-ins',
      'data-author': HTMLAttributes.author,
      'data-date': HTMLAttributes.date,
      'data-reason': HTMLAttributes.reason || '',
      title: renderTitle(HTMLAttributes, 'Inserted'),
    }), 0];
  },
});
 
export const Deletion = Mark.create({
  name: 'deletion',
  inclusive: false,
  addAttributes() { return authorAttrs; },
  parseHTML() { return [{ tag: 'del' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['del', mergeAttributes(HTMLAttributes, {
      class: 'tc-del',
      'data-author': HTMLAttributes.author,
      'data-date': HTMLAttributes.date,
      'data-reason': HTMLAttributes.reason || '',
      title: renderTitle(HTMLAttributes, 'Deleted'),
    }), 0];
  },
});
 
// --- Auto-track plugin -----------------------------------------------------
// A shared mutable context so the plugin always knows the current user,
// whether tracking is on, and the "reason" to attach to the next change.
export const trackCtx = {
  enabled: true,
  user: { id: 'unknown', name: 'Unknown' },
  reason: '',
  onRevision: null, // callback(revisionRecord) -> persist to audit
};
 
const trackKey = new PluginKey('autoTrack');
 
export function AutoTrackPlugin() {
  return new Plugin({
    key: trackKey,
    appendTransaction(transactions, oldState, newState) {
      if (!trackCtx.enabled) return null;
      const docChanged = transactions.some((t) => t.docChanged);
      if (!docChanged) return null;
 
      // Find inserted ranges in this set of transactions and stamp them with
      // the insertion mark carrying author/date/reason.
      const tr = newState.tr;
      let touched = false;
      const insMark = newState.schema.marks.insertion;
      if (!insMark) return null;
 
      transactions.forEach((transaction) => {
        transaction.steps.forEach((step) => {
          const map = step.getMap();
          map.forEach((fromA, toA, fromB, toB) => {
            if (toB > fromB) {
              const attrs = {
                author: trackCtx.user.name,
                authorId: trackCtx.user.id,
                date: new Date().toISOString(),
                reason: trackCtx.reason || '',
              };
              tr.addMark(fromB, toB, insMark.create(attrs));
              touched = true;
              if (trackCtx.onRevision) {
                const text = newState.doc.textBetween(fromB, toB, ' ');
                trackCtx.onRevision({
                  type: 'insertion', text,
                  author: attrs.author, authorId: attrs.authorId,
                  date: attrs.date, reason: attrs.reason,
                });
              }
            }
          });
        });
      });
 
      // Clear one-shot reason after it's been applied.
      if (touched) { trackCtx.reason = ''; return tr; }
      return null;
    },
  });
}
 
// Mark a selection as deleted (struck through) instead of removing it.
export function markDeletion(editor, user, reason = '') {
  const { from, to } = editor.state.selection;
  if (from === to) return;
  const text = editor.state.doc.textBetween(from, to, ' ');
  editor.chain().focus()
    .setMark('deletion', {
      author: user.name, authorId: user.id,
      date: new Date().toISOString(), reason,
    })
    .run();
  if (trackCtx.onRevision) {
    trackCtx.onRevision({
      type: 'deletion', text,
      author: user.name, authorId: user.id,
      date: new Date().toISOString(), reason,
    });
  }
}
 
// Accept all: drop insertion marks (keep text), remove deletion-marked text.
export function acceptAll(editor) {
  const { state, view } = editor;
  const tr = state.tr;
  const dels = [];
  state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    node.marks.forEach((m) => {
      if (m.type.name === 'insertion') tr.removeMark(pos, pos + node.nodeSize, m.type);
      if (m.type.name === 'deletion') dels.push([pos, pos + node.nodeSize]);
    });
  });
  // delete from the back so positions stay valid
  dels.sort((a, b) => b[0] - a[0]).forEach(([f, t]) => tr.delete(f, t));
  view.dispatch(tr);
}
 
// Reject all: remove insertion-marked text, drop deletion marks (keep text).
export function rejectAll(editor) {
  const { state, view } = editor;
  const tr = state.tr;
  const ins = [];
  state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    node.marks.forEach((m) => {
      if (m.type.name === 'deletion') tr.removeMark(pos, pos + node.nodeSize, m.type);
      if (m.type.name === 'insertion') ins.push([pos, pos + node.nodeSize]);
    });
  });
  ins.sort((a, b) => b[0] - a[0]).forEach(([f, t]) => tr.delete(f, t));
  view.dispatch(tr);
}
 
// Convert the current editor document into the flat node/run list docx.js needs.
export function editorToExportNodes(editor) {
  const json = editor.getJSON();
  const nodes = [];
  const walk = (content) => (content || []);
  walk(json.content).forEach((block) => {
    if (block.type === 'heading') {
      nodes.push({
        type: 'heading',
        level: block.attrs?.level || 1,
        text: walk(block.content).map((c) => c.text || '').join(''),
      });
    } else if (block.type === 'paragraph') {
      nodes.push({
        type: 'paragraph',
        runs: walk(block.content).map((c) => ({
          text: c.text || '',
          marks: (c.marks || []).map((m) => m.type),
          author: (c.marks || []).find((m) => m.attrs?.author)?.attrs?.author,
          date: (c.marks || []).find((m) => m.attrs?.date)?.attrs?.date,
          reason: (c.marks || []).find((m) => m.attrs?.reason)?.attrs?.reason,
        })),
      });
    } else if (block.type === 'table') {
      // pass tables through with a flag; docx.js renders explicit-width tables
      nodes.push({ type: 'table', explicitWidths: true, raw: block });
    }
  });
  return nodes;
}
 